/**
 * requestors + requestor_assignments (OPEN-053, ClickUp 86bc5hjn1 — migration 0087).
 *
 * WHO ASKED for a load, as an entity instead of three free-text columns retyped on every
 * ticket. Two named slots per site/well; the job records which of the two actually requested
 * the load (`jobs.requestorId`).
 *
 * Hand-written to match `0087_requestors_and_assignments.sql` exactly — drizzle-kit generate
 * cannot run in this repo (the meta snapshot chain is stale), so a drift between this file
 * and the SQL is silent. A shared test asserts the two column sets agree.
 *
 * Two shape decisions worth knowing before editing:
 *
 * 1. NOT an extension of `site_contacts` (0072), despite the near-identical shape. That
 *    table's only consumer is the urgent-email recipient query, so every row added there
 *    becomes someone who gets emailed on load completion — which a requestor is not
 *    necessarily. It also demands an operator_id and an email; requestors are tenant-global
 *    (consultants, landmen) and frequently phone-only.
 *
 * 2. A junction with a `slot`, NOT requestor_1_id / requestor_2_id columns on the parents.
 *    `wells` is a GLOBAL catalog table with no tenant_id and no RLS, so a requestor id stored
 *    on it is readable by every tenant. The junction carries its own tenantId (rather than
 *    inheriting transitively as site_contact_assignments does) so a well assignment stays
 *    tenant-isolated even though the well it points at is not.
 */

import {
  boolean,
  check,
  foreignKey,
  index,
  pgPolicy,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { tenants } from './users.js';
import { appUserRole } from './enums.js';
import { haulerLocations, wells } from './platform-catalog.js';
import { importBatches } from './imports.js';

export const requestors = pgTable(
  'requestors',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    /**
     * Contact info, deliberately nullable — field requestors are often phone-only. NOT
     * uppercased: `jobs.ts` already documents requester contact info as exempt from the
     * DEC-147 identifier rule, and an uppercased email address is simply wrong.
     */
    phone: text('phone'),
    email: text('email'),
    /**
     * Free text rather than an FK to hauler_operators — a requestor may work for a
     * consultant or third-party pumper, not the hauler's customer. This one IS a
     * hauler-entered identifier, so it is uppercased server-side (DEC-147).
     */
    company: text('company'),
    isActive: boolean('is_active').notNull().default(true),
    /**
     * Phase 17 / DEC-324 D-13 (migration 0147) — the per-requestor "send load emails"
     * checkbox, default ON; only consulted when the hauler-wide switch
     * (hauler_tenant_settings.requestor_load_emails_enabled) is on.
     */
    loadEmailsEnabled: boolean('load_emails_enabled').notNull().default(true),
    /** D-09 — set only by the requestor's own unsubscribe link; no hauler route clears it. */
    unsubscribedAt: timestamp('unsubscribed_at', { withTimezone: true }),
    /** 17.6 / DEC-324 D-11 (migration 0152) — 'OK' | 'BOUNCING' | 'COMPLAINED'. */
    emailStatus: text('email_status').notNull().default('OK'),
    emailStatusAt: timestamp('email_status_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('requestors_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'requestors_import_batch_tenant_fk',
    }).onDelete('set null'),
    index('requestors_tenant_idx').on(t.tenantId),
    pgPolicy('requestors_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export const requestorAssignments = pgTable(
  'requestor_assignments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /**
     * RESTRICT, not CASCADE: deactivating a requestor (`isActive`) is the supported path.
     * Cascading would let a delete quietly strip the person who requests a site's loads.
     */
    requestorId: uuid('requestor_id')
      .notNull()
      .references(() => requestors.id, { onDelete: 'restrict' }),
    assignmentType: text('assignment_type').notNull(),
    wellId: uuid('well_id').references(() => wells.id, { onDelete: 'cascade' }),
    haulerLocationId: uuid('hauler_location_id').references(() => haulerLocations.id, {
      onDelete: 'cascade',
    }),
    /**
     * "Exactly 2" as a constraint. Slot 2 may be filled while slot 1 is empty — an ordering
     * rule would force a bulk clear of slot 1 to cascade-promote slot 2, which is surprising
     * and hard to undo.
     */
    slot: smallint('slot').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('requestor_assignments_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'requestor_assignments_import_batch_tenant_fk',
    }).onDelete('set null'),
    // Partial, because the target is polymorphic: one index over both columns would
    // constrain neither case, since NULLs never conflict.
    uniqueIndex('requestor_assignments_well_slot_key')
      .on(t.tenantId, t.wellId, t.slot)
      .where(sql`well_id IS NOT NULL`),
    uniqueIndex('requestor_assignments_location_slot_key')
      .on(t.tenantId, t.haulerLocationId, t.slot)
      .where(sql`hauler_location_id IS NOT NULL`),
    index('requestor_assignments_requestor_idx').on(t.requestorId),
    index('requestor_assignments_tenant_idx').on(t.tenantId),
    check('requestor_assignments_slot_check', sql`slot IN (1, 2)`),
    check('requestor_assignments_type_check', sql`assignment_type IN ('WELL', 'TANK_BATTERY')`),
    check(
      'requestor_assignments_target_xor',
      sql`(assignment_type = 'WELL' AND well_id IS NOT NULL AND hauler_location_id IS NULL)
          OR (assignment_type = 'TANK_BATTERY' AND hauler_location_id IS NOT NULL AND well_id IS NULL)`,
    ),
    pgPolicy('requestor_assignments_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

/** The two assignment slots a site or well supports. */
export const REQUESTOR_SLOTS = [1, 2] as const;
export type RequestorSlot = (typeof REQUESTOR_SLOTS)[number];

/** Which kind of thing an assignment points at. */
export const REQUESTOR_ASSIGNMENT_TYPES = ['WELL', 'TANK_BATTERY'] as const;
export type RequestorAssignmentType = (typeof REQUESTOR_ASSIGNMENT_TYPES)[number];
